// fcmOperations.js
// Low-level FCM token operations utility
// Used by FCMNotificationBanner (onboarding) and useNotificationActions (pause/resume)

import { saveFCMToken, deleteFCMToken } from "../services/api";
import { API_URLS } from "../constants/apiUrls";
import { getFcmToken } from "./fcmTokenManager";
import { NOTIFICATION_STORAGE_KEYS } from "../constants/Notification";

/**
 * Get the Firebase service worker registration
 * @returns {Promise<ServiceWorkerRegistration>} - The service worker registration
 */
export const getFirebaseServiceWorkerRegistration = async () => {
  const registrations = await navigator.serviceWorker.getRegistrations();
  let registration = registrations.find(
    (reg) => reg.active && reg.active.scriptURL.includes("firebase-messaging-sw.js")
  );

  if (!registration) {
    registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  }

  return registration;
};

/**
 * Get FCM token from service worker
 * @returns {Promise<string|null>} - The FCM token or null if unavailable
 */
export const getFcmTokenFromServiceWorker = async () => {
  const registration = await getFirebaseServiceWorkerRegistration();
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  return await getFcmToken(registration, vapidKey);
};

/**
 * Register (save) FCM token to the server and localStorage
 * @param {string} authToken - The authentication token
 * @param {string} fcmToken - The FCM token to register
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const registerFcmToken = async (authToken, fcmToken) => {
  try {
    await saveFCMToken(API_URLS.FCM_SAVE, authToken, fcmToken);
    localStorage.setItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN, fcmToken);
    console.log("FCM token registered successfully");
    return true;
  } catch (error) {
    console.error("Failed to register FCM token", error);
    return false;
  }
};

/**
 * Unregister (delete) FCM token from the server and localStorage
 * @param {string} authToken - The authentication token
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const unregisterFcmToken = async (authToken) => {
  try {
    const fcmToken = localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
    if (fcmToken) {
      await deleteFCMToken(API_URLS.FCM_DELETE, authToken, fcmToken);
      localStorage.removeItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
      console.log("FCM token de-registered successfully");
    }
    return true;
  } catch (error) {
    console.error("Failed to de-register FCM token", error);
    return false;
  }
};

/**
 * Unregister all Firebase service workers
 * @returns {Promise<void>}
 */
export const unregisterFirebaseServiceWorkers = async () => {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
    console.log("Service worker unregistered successfully");
  }
};

/**
 * Check if FCM/Notifications are supported in current browser
 * @returns {boolean} - True if supported
 */
export const isFcmSupported = () => {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator
  );
};

/**
 * Get stored FCM token from localStorage
 * @returns {string|null} - The stored FCM token or null
 */
export const getStoredFcmToken = () => {
  return localStorage.getItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
};

/**
 * Clear stored FCM token from localStorage
 */
export const clearStoredFcmToken = () => {
  localStorage.removeItem(NOTIFICATION_STORAGE_KEYS.FCM_TOKEN);
};
